import React, { useMemo } from 'react';
import {
  AnalysisModel, Metric, PlayerModel, RoundFilter, coursePerformance, fmtSigned, fmtToPar, holeFieldStats,
} from '../../utils/majorAnalysis';
import { divergingColor, inkOn } from '../../utils/chartPalette';
import { shortDate } from './DataNeeded';
import { PlayerCell, RankCell, SortTh, fmt1, sortRows, useSort } from './shared';

interface Props {
  model: AnalysisModel;
  players: PlayerModel[];
  metric: Metric;
  filter: RoundFilter;
}

const CoursePerformance: React.FC<Props> = ({ model, players, metric, filter }) => {
  const { summaries, rows } = useMemo(() => coursePerformance(model, metric, filter), [model, metric, filter]);
  const { sort, toggle } = useSort();
  const byId = new Map(rows.map((r) => [r.player_id, r]));
  const nameOf = (id: number) => model.players.find((p) => p.player_id === id)?.first_name ?? '';
  const scale = Math.max(1, ...rows.flatMap((r) => Object.values(r.cells).map((c) => Math.abs(c.vsField ?? 0))));
  const sorted = sortRows(players, sort, (p) => p.order, (p, key) => {
    const r = byId.get(p.player_id);
    if (!r) return null;
    if (key === 'overall') return r.overall;
    if (key === 'rounds') return r.rounds;
    return r.cells[key]?.avg ?? null;
  });

  return (
    <div className="ma-tab">
      <p className="ma-lede">
        {summaries.length} course{summaries.length === 1 ? '' : 's'} in this major. Each cell is a player’s average {metric} score there,
        shaded against the field’s average on that course (blue = better than the field, red = worse).
      </p>

      <div className="ma-course-cards">
        {summaries.map((s) => {
          const holes = holeFieldStats(model, s.course.key, 'gross').filter((h) => h.avgToPar != null && h.n >= 2);
          const hardest = holes.length >= 2 ? holes.reduce((b, h) => ((h.avgToPar as number) > (b.avgToPar as number) ? h : b)) : null;
          const easiest = holes.length >= 2 ? holes.reduce((b, h) => ((h.avgToPar as number) < (b.avgToPar as number) ? h : b)) : null;
          return (
            <div key={s.course.key} className="ma-course-card">
              <div className="ma-course-name">{s.course.course_name}</div>
              <div className="ma-course-sub">
                {s.course.teebox_name && <span>{s.course.teebox_name}</span>}
                {s.course.par != null && <span>Par {s.course.par}</span>}
                {s.course.rating != null && <span>{s.course.rating} / {s.course.slope}</span>}
              </div>
              <dl className="ma-kv">
                <div><dt>Played</dt><dd>{s.playerRounds} round{s.playerRounds === 1 ? '' : 's'} · {s.matches} match{s.matches === 1 ? '' : 'es'}</dd></div>
                <div><dt>Field average</dt><dd>{fmt1(s.fieldAvg)} {metric}{s.vsPar != null ? ` · ${fmtToPar(s.vsPar)} gross vs par` : ''}</dd></div>
                {s.low && <div><dt>Low round</dt><dd>{s.low.value} · {nameOf(s.low.player_id)} (R{s.low.round}, {shortDate(s.low.date)})</dd></div>}
                {s.high && s.playerRounds > 1 && <div><dt>High round</dt><dd>{s.high.value} · {nameOf(s.high.player_id)} (R{s.high.round})</dd></div>}
                {hardest && <div><dt>Hardest hole</dt><dd>#{hardest.hole} (par {hardest.par}) · {fmtToPar(hardest.avgToPar)}</dd></div>}
                {easiest && <div><dt>Easiest hole</dt><dd>#{easiest.hole} (par {easiest.par}) · {fmtToPar(easiest.avgToPar)}</dd></div>}
              </dl>
            </div>
          );
        })}
      </div>

      <div className="ma-scroll">
        <table className="tbl tbl--sm ma-table ma-table--sticky">
          <thead>
            <tr>
              <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
              <th className="ma-stick-2">Player</th>
              {summaries.map((s) => (
                <SortTh key={s.course.key} sortKey={s.course.key} sort={sort} onSort={toggle} className="c"
                  title={`${s.course.label} — average ${metric} (rounds)`} label={s.course.course_name} />
              ))}
              <SortTh label="Overall" sortKey="overall" sort={sort} onSort={toggle} className="c" title={`Average ${metric} over every round`} />
              <th className="c" title="Where they gained most on the field (needs two courses)">Best course</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const r = byId.get(p.player_id);
              return (
                <tr key={p.player_id}>
                  <RankCell p={p} all={model.players} />
                  <PlayerCell p={p} />
                  {summaries.map((s) => {
                    const c = r?.cells[s.course.key];
                    if (!c) return <td key={s.course.key} className="c ma-nil">—</td>;
                    const bg = c.vsField == null ? undefined : divergingColor(-c.vsField, scale);
                    return (
                      <td key={s.course.key} className="c ma-heat" style={bg ? { background: bg, color: inkOn(bg) } : undefined}
                        title={`${p.first_name} on ${s.course.label}: ${fmt1(c.avg)} average over ${c.n} round${c.n === 1 ? '' : 's'} (best ${c.best}), ${fmtSigned(c.vsField)} vs the field`}>
                        <span className="ma-heat-main">{fmt1(c.avg)}</span>
                        <span className="ma-heat-sub">{c.n > 1 ? `${c.n} rds · ` : ''}{fmtSigned(c.vsField)}</span>
                      </td>
                    );
                  })}
                  <td className="c ma-strong">{fmt1(r?.overall)}</td>
                  <td className="c">{r?.bestCourse ? summaries.find((s) => s.course.key === r.bestCourse)?.course.course_name : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="ma-foot">Numbers under each average: rounds played there (when more than one) and strokes vs the field’s average on that course.</p>
    </div>
  );
};

export default CoursePerformance;
